import { ComponentProps, ReactNode, cloneElement, forwardRef, isValidElement } from 'react';

import { Button as DsButton, IconButton as DsIconButton, Tooltip as DsTooltip } from '@natter-co/design-system';
import { Tooltip } from 'common/components/Tooltip';
import { VISUAL_REFRESH } from 'common/utils';

import { StyleButton } from './ActionButton.styled';
import { toDsActionProps } from './toDsActionProps';

/**
 * The Design System 2.0 cell this control takes under the visual refresh, for the buttons whose
 * legacy look is painted with `sx`. See `toDsActionProps` for why declaring it is required.
 *
 * TODO(HNS-264): deleted with the flag, along with the `sx` each one stands in for.
 */
export type ActionButtonAppearance =
  | 'primary'
  | 'primary-muted'
  | 'primary-selected'
  | 'secondary'
  | 'destructive'
  | 'destructive-selected';

export type ActionButtonProps = ComponentProps<typeof StyleButton> & {
  label: string;
  /** DS notification presentation; legacy consumers keep their existing MUI badge. */
  badge?: ComponentProps<typeof DsIconButton>['badge'];
  isDisabled?: boolean;
  'data-testid'?: string;
  Icon?: ReactNode;
  dsAppearance?: ActionButtonAppearance;
  /** Filled glyph an icon-only toggle fades into while selected. Ignored on the legacy bar. */
  selectedIcon?: ReactNode;
  /**
   * The DS toggle morph: selected, the button tightens to a softer shape, lifts and fades
   * `selectedIcon` in. On by default for a toggle (one with a `selectedIcon`), off otherwise, as the
   * ControlBar spec draws it. A toggle inside a split button passes `false`: the split keeps its own
   * shape, and the button shows `selectedIcon` in place of its icon while selected instead.
   */
  morph?: boolean;
};

/**
 * The legacy icons size themselves (`fontSize="medium"` is 24px, set by Emotion on the `<svg>`),
 * which beats the DS button's 20px icon slot. `inherit` lets the slot's font size decide.
 */
const toDsGlyph = (icon: ReactNode) =>
  isValidElement<{ fontSize?: string }>(icon) && icon.props.fontSize !== undefined
    ? cloneElement(icon, { fontSize: 'inherit' })
    : icon;

/**
 * The ref reaches the underlying `<button>` in both worlds, so the control can be a Popover trigger
 * (`<PopoverTrigger render={<ActionButton … />} />`), which positions against and toggles from it.
 */
export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(function ActionButton(
  {
    label,
    isDisabled,
    isLoading,
    children,
    Icon = children,
    color,
    dsAppearance,
    selectedIcon,
    morph,
    badge,
    ...props
  },
  ref
) {
  /**
   * The visual-refresh gate lives HERE, not at the call sites — they keep this component's
   * MUI-shaped API. `toDsActionProps` returns `null` for anything DS 2.0 cannot express, which falls
   * through to the MUI implementation below.
   *
   * The DS button is a 44px circle rather than a 48px rounded square, which is the point of the swap
   * (Figma 18187:17601).
   */
  const dsProps = VISUAL_REFRESH ? toDsActionProps({ children, dsAppearance, isLoading, ...props }) : null;

  if (dsProps) {
    const { labelled, ...dsButtonProps } = dsProps;
    // Dropped because they are meaningless on a component with no Emotion and no ripple. `size`
    // additionally has to go: both APIs spell the prop the same way but mean different heights by
    // it, so forwarding the legacy value would fight the mapped one. `isActive` and `isSelected`
    // are legacy-only — the DS side says the same thing through `dsAppearance`.
    const { disabled, isActive: _isActive, isSelected: _isSelected, size: _size, startIcon, sx: _sx, ...rest } = props;

    const isButtonDisabled = disabled || isDisabled;
    const shouldMorph = morph ?? selectedIcon !== undefined;
    const glyph = !shouldMorph && dsButtonProps.isSelected && selectedIcon !== undefined ? selectedIcon : Icon;
    const control = labelled ? (
      <DsButton
        ref={ref}
        {...rest}
        {...dsButtonProps}
        disabled={isButtonDisabled}
        focusableWhenDisabled={isButtonDisabled || isLoading || undefined}
        startIcon={toDsGlyph(startIcon)}
      >
        {children}
      </DsButton>
    ) : (
      <DsIconButton
        ref={ref}
        {...rest}
        {...dsButtonProps}
        morph={shouldMorph}
        selectedIcon={shouldMorph ? toDsGlyph(selectedIcon) : undefined}
        disabled={isButtonDisabled}
        focusableWhenDisabled={isButtonDisabled || isLoading || undefined}
        aria-label={label}
        badge={badge}
      >
        {toDsGlyph(glyph)}
      </DsIconButton>
    );

    return (
      // Button and IconButton replace the trigger node when labels collapse. Rebind the tooltip's
      // hover listeners to that node; the owning control and its confirmation stay mounted.
      <DsTooltip key={labelled ? 'labelled' : 'icon'} label={label} side="top">
        {control}
      </DsTooltip>
    );
  }

  return (
    <Tooltip title={label} placement="top" variant="dark" display="block">
      <StyleButton
        ref={ref}
        color={color}
        disabled={isDisabled}
        aria-label={label}
        disableFocusRipple
        disableRipple
        loading={isLoading}
        {...props}
      >
        {Icon}
      </StyleButton>
    </Tooltip>
  );
});
